import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import { AlertTriangle, Copy, KeyRound } from "lucide-react";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import {
  regeneratePrestashopKey,
  regenerateWoocommerceKey,
} from "../../../../api/integrations";
import "../ModernIntegrations.scss";

// Config por plataforma: endpoint de rotación + dónde pegar la clave.
const PLATFORMS = {
  WooCommerce: {
    regenerate: regenerateWoocommerceKey,
    settingsPath: "WooCommerce → Beartrack",
  },
  Prestashop: {
    regenerate: regeneratePrestashopKey,
    settingsPath: "Módulos → BearTrack → Configurar",
  },
};

// Clave de conexión del plugin/módulo para una tienda YA integrada
// (WooCommerce o PrestaShop): cubre tiendas registradas antes de que existiera
// la clave y la rotación manual. La clave no se puede "ver": solo generar una
// nueva (la anterior queda inválida al instante), así que el flujo es generar
// → copiar → pegarla en los ajustes de la tienda.
export default function WooConnectionKeyModal({
  open,
  account,
  onClose,
  platform = "WooCommerce",
}) {
  const { t } = useTranslation();
  const [generating, setGenerating] = useState(false);
  const [connectionKey, setConnectionKey] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const cfg = PLATFORMS[platform] ?? PLATFORMS.WooCommerce;
  const storeUrl = account?.url || account?.sender_id || "";
  const label = (account?.alias || storeUrl).replace(/^https?:\/\//, "");

  const handleGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    setErrorMessage("");
    try {
      const res = await cfg.regenerate({ url: storeUrl });
      const key = res?.data?.connectionKey;
      if (res && "error" in res) {
        console.error(res.error);
        setErrorMessage(t("No se pudo generar la clave. Intenta nuevamente."));
      } else if (key) {
        setConnectionKey(key);
      } else {
        setErrorMessage(t("Respuesta inesperada del servidor"));
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(t("No se pudo generar la clave. Intenta nuevamente."));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t("Clave de conexión del plugin")}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {connectionKey ? t("Listo") : t("Cancelar")}
          </Button>
          <Button
            variant="primary"
            onClick={handleGenerate}
            loading={generating}
            iconLeft={<KeyRound className="h-4 w-4" aria-hidden="true" />}
          >
            {connectionKey ? t("Generar otra") : t("Generar nueva clave")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-fg-muted">
          {t(
            "La clave autentica al plugin de {{label}} con BearTrack. Por seguridad no se puede consultar: solo generar una nueva.",
            { label },
          )}
        </p>

        <div
          className="flex items-start gap-2 rounded-lg border border-border bg-bg-muted px-4 py-3"
          role="note"
        >
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-status-warning"
            aria-hidden="true"
          />
          <p className="text-sm text-fg-muted">
            {t(
              "Al generar una clave nueva, la anterior deja de funcionar de inmediato: recuerda actualizarla en {{path}}.",
              { path: t(cfg.settingsPath) },
            )}
          </p>
        </div>

        {connectionKey && (
          <div className="space-y-1.5 rounded-lg border border-border bg-bg-muted px-4 py-3">
            <p className="text-sm font-semibold text-fg">{t("Nueva clave de conexión")}</p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-bg-subtle px-2 py-1.5 font-mono text-xs text-fg">
                {connectionKey}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard
                    ?.writeText(connectionKey)
                    .then(() => toast.success(t("Clave copiada")))
                    .catch(() => toast.error(t("No se pudo copiar la clave")));
                }}
                iconLeft={<Copy className="h-3.5 w-3.5" aria-hidden="true" />}
              >
                {t("Copiar")}
              </Button>
            </div>
            <p className="text-xs text-fg-muted">
              {t("Guárdala ahora: solo se muestra esta vez.")}
            </p>
          </div>
        )}

        {errorMessage && (
          <p className="text-sm text-status-error" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
